import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Coins, ArrowUp, ArrowDown, ArrowUpDown, Download, Printer, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import PdfModeModal, { getSavedPdfMode } from '@/components/PdfModeModal';
import { exportNodeAsPdf } from '@/lib/pdfExport';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SortHeader({ label, col, sort, onSort, align = 'left', testId }) {
  const active = sort.col === col;
  const Icon = !active ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold ${active ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground ${align === 'right' ? 'justify-end w-full' : ''}`}
      data-testid={testId}
      aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      {label}
      <Icon className="w-3 h-3" />
    </button>
  );
}

const esc = (v) => String(v ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));

export default function DividendsCollectedModal({ open, onClose, portfolioId, total, portfolioName }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ col: 'date', dir: 'desc' });
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [year, setYear] = useState('all');
  const scopeLabel = portfolioName || (portfolioId ? 'Selected portfolio' : 'All portfolios');
  const stamp = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/dividends-collected`, { params: portfolioId ? { portfolio_id: portfolioId } : {}, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ rows: [], total: 0, count: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, portfolioId]);

  const onSort = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: col === 'name' ? 'asc' : 'desc' }));

  const years = useMemo(() => {
    const ys = new Set((data?.rows || []).map((r) => (r.date || '').slice(0, 4)).filter((y) => /^\d{4}$/.test(y)));
    return [...ys].sort((a, b) => b.localeCompare(a));
  }, [data]);
  const rows = useMemo(() => {
    const list = (data?.rows || []).filter((r) => year === 'all' || (r.date || '').startsWith(year));
    const dir = sort.dir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      if (sort.col === 'amount') return (a.amount - b.amount) * dir;
      if (sort.col === 'name') return ((a.ticker || a.name || '') > (b.ticker || b.name || '') ? 1 : -1) * dir;
      return ((a.date || '') > (b.date || '') ? 1 : (a.date || '') < (b.date || '') ? -1 : 0) * dir;
    });
    return list;
  }, [data, sort, year]);
  const shownTotal = useMemo(() => rows.reduce((a, r) => a + Number(r.amount || 0), 0), [rows]);
  const yearLabel = year === 'all' ? 'All years' : `Tax year ${year}`;

  const handleCsv = () => {
    if (!rows.length) return;
    const lines = [['Ticker', 'Name', 'Paid', 'Amount', 'Type'].join(',')];
    rows.forEach((r) => lines.push([
      r.ticker || '', `"${String(r.name || '').replace(/"/g, '""')}"`, r.date || '', Number(r.amount).toFixed(2), `${r.kind === 'drip' ? 'DRIP reinvested' : 'Cash'}${r.is_roc ? ' — ROC (return of capital)' : ''}`,
    ].join(',')));
    lines.push(['TOTAL', yearLabel, '', shownTotal.toFixed(2), `${rows.length} payments`].join(','));
    const blob = new Blob([`${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `dividrip-dividends-collected-${year === 'all' ? 'all-years' : year}-${stamp}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success('Dividends CSV downloaded');
  };

  // Print: own light-theme window (never the modal-laden app page) —
  // rows can't split across pages, header repeats on every page.
  const handlePrint = () => {
    if (!rows.length) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { toast.error('Print blocked: allow pop-ups for this site.'); return; }
    const body = rows.map((r) => `<tr><td><b>${esc(r.ticker || '—')}</b>${r.kind === 'drip' ? ' <span class="tag">DRIP</span>' : ''}${r.is_roc ? ' <span class="tag roc">ROC</span>' : ''}<br><span class="nm">${esc(r.name || '')}</span></td><td class="mono">${esc(r.date || '—')}</td><td class="mono amt">${fmt(r.amount)}</td></tr>`).join('');
    win.document.open();
    win.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8" /><title>Dividends Collected — ${esc(yearLabel)} — ${esc(scopeLabel)}</title>
<style>
  body { font-family: Helvetica, Arial, sans-serif; color: #111; background: #fff; margin: 24px; line-height: 1.4; }
  h1 { font-size: 18px; margin: 0 0 4px; } .meta { font-size: 11px; color: #666; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 14px; }
  .total { font-size: 14px; margin: 0 0 12px; } .total b { color: #059669; font-family: Menlo, Consolas, monospace; }
  table { border-collapse: collapse; width: 100%; } thead { display: table-header-group; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #555; border-bottom: 1px solid #999; padding: 6px 8px; }
  td { border-bottom: 1px solid #e5e5e5; padding: 6px 8px; font-size: 12px; vertical-align: top; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  .mono { font-family: Menlo, Consolas, monospace; white-space: nowrap; } .amt { text-align: right; color: #059669; font-weight: bold; } th.amt { text-align: right; }
  .nm { color: #666; font-size: 11px; } .tag { font-size: 9px; border: 1px solid #7dd3fc; color: #0369a1; border-radius: 3px; padding: 0 3px; } .tag.roc { border-color: #fcd34d; color: #92400e; }
  @page { margin: 16mm 14mm; }
</style></head><body>
<h1>Dividends Collected</h1>
<div class="meta"><b>${esc(scopeLabel)}</b> &nbsp;·&nbsp; <b>${esc(yearLabel)}</b> &nbsp;·&nbsp; ${rows.length} payments &nbsp;·&nbsp; Printed via twylightcrow.com on ${new Date().toLocaleDateString()}${user?.name || user?.email ? ` for ${esc(user.name || user.email)}` : ''}</div>
<p class="total">${year === 'all' ? 'Lifetime dividends collected' : `Dividends collected in ${year}`}: <b>${fmt(shownTotal)}</b></p>
<table><thead><tr><th>Name</th><th>Paid</th><th class="amt">Amount</th></tr></thead><tbody>${body}</tbody></table>
</body></html>`);
    win.document.close();
    setTimeout(() => { try { win.focus(); win.print(); } catch { /* cancelled */ } }, 400);
  };

  const handlePdf = async (mode = 'download') => {
    setPdfBusy(true);
    try {
      await exportNodeAsPdf({ elementId: 'divs-printable', filename: `dividrip-dividends-collected-${year === 'all' ? 'all-years' : year}-${stamp}.pdf`, mode, user });
    } finally {
      setPdfBusy(false);
    }
  };

  const light = { color: '#0f172a', background: '#ffffff' };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl w-[95vw] sm:w-auto max-h-[88vh] overflow-hidden flex flex-col pb-20 sm:pb-4"
        data-testid="dividends-collected-modal"
        onInteractOutside={(e) => { if (e.target?.closest?.('[data-testid="dividends-pdf-mode-wrap"]')) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (e.target?.closest?.('[data-testid="dividends-pdf-mode-wrap"]')) e.preventDefault(); }}
        onFocusOutside={(e) => { if (e.target?.closest?.('[data-testid="dividends-pdf-mode-wrap"]')) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Dividends Collected
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Every dividend this portfolio has produced — cash payouts plus dividends reinvested through DRIP (tagged). Payments your broker coded as return of capital carry an ROC tag so tax prep can separate them. Portfolio income, never a contribution.
          </p>
        </DialogHeader>

        <div className="rounded-lg border bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 px-4 py-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{year === 'all' ? 'Lifetime dividends collected' : `Dividends collected in ${year}`}</p>
          <p className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-400" data-testid="dividends-collected-total">
            {fmt(data ? shownTotal : total)}
          </p>
          <p className="text-[11px] text-muted-foreground" data-testid="dividends-collected-count">
            {data ? `${rows.length} payment${rows.length === 1 ? '' : 's'}${year !== 'all' ? ` · lifetime ${fmt(data.total)}` : ''}` : ''}
          </p>
        </div>

        <div className="flex items-center justify-between gap-1.5 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            Tax year
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="h-8 rounded-lg border bg-card px-2 text-xs font-semibold text-foreground"
              data-testid="dividends-year-select"
            >
              <option value="all">All years</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1.5 flex-wrap">
          <button type="button" onClick={handleCsv} disabled={!rows.length} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border bg-card hover:bg-muted text-xs font-semibold transition-colors disabled:opacity-50" data-testid="dividends-csv-btn" title="Download this list as CSV (tax prep)">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button type="button" onClick={() => { const saved = getSavedPdfMode(); if (saved) handlePdf(saved); else setPdfModalOpen(true); }} disabled={!rows.length || pdfBusy} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border bg-card hover:bg-muted text-xs font-semibold transition-colors disabled:opacity-50" data-testid="dividends-pdf-btn" title="Download the PDF or open it in a new tab">
            <FileDown className="w-3.5 h-3.5" /> {pdfBusy ? 'Building…' : 'Save/View PDF'}
          </button>
          <button type="button" onClick={handlePrint} disabled={!rows.length} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border bg-card hover:bg-muted text-xs font-semibold transition-colors disabled:opacity-50" data-testid="dividends-print-btn" title="Print this list">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_6.5rem_6rem] gap-x-3 px-2 pt-2 pb-1 border-b">
          <SortHeader label="Name" col="name" sort={sort} onSort={onSort} testId="dividends-sort-name" />
          <SortHeader label="Paid" col="date" sort={sort} onSort={onSort} align="right" testId="dividends-sort-date" />
          <SortHeader label="Amount" col="amount" sort={sort} onSort={onSort} align="right" testId="dividends-sort-amount" />
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 -mx-1 px-1" data-testid="dividends-collected-list">
          {loading && !data ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="dividends-collected-empty">{year === 'all' ? 'No dividends recorded for this portfolio yet.' : `No dividends recorded in ${year}.`}</p>
          ) : (
            rows.map((r, i) => (
              <div
                key={r.tx_id || `${r.ticker}-${r.date}-${i}`}
                className="grid grid-cols-[1fr_6.5rem_6rem] gap-x-3 items-center px-2 py-1.5 border-b border-border/60 last:border-b-0"
                data-testid={`dividends-row-${r.ticker || 'na'}-${r.date}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold font-mono leading-tight">
                    {r.ticker || '—'}
                    {r.kind === 'drip' && (
                      <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-bold border border-sky-300 dark:border-sky-800 text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30">DRIP</span>
                    )}
                    {r.is_roc && (
                      <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-bold border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30" title="Return of capital — reduces your cost basis rather than being taxed as an ordinary dividend" data-testid="dividends-roc-tag">ROC</span>
                    )}
                  </p>
                  {r.name && <p className="text-[11px] text-muted-foreground truncate">{r.name}</p>}
                </div>
                <p className="text-xs font-mono text-muted-foreground whitespace-nowrap text-right tabular-nums">{r.date || '—'}</p>
                <p className="text-sm font-bold font-mono text-emerald-700 dark:text-emerald-400 text-right whitespace-nowrap tabular-nums">{fmt(r.amount)}</p>
              </div>
            ))
          )}
        </div>

        {/* Off-screen light-theme copy captured by html2pdf — inline colours
            so the PDF is legible regardless of the app's dark/light theme. */}
      </DialogContent>
      {open && createPortal(
        <div style={{ position: 'absolute', left: '-10000px', top: 0, width: '720px', ...light }} aria-hidden="true">
          <div id="divs-printable" data-pdf-root style={{ ...light, padding: '8px 12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>
            <h1 style={{ fontSize: 18, margin: '0 0 4px', color: '#0f172a' }}>Dividends Collected</h1>
            <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px', borderBottom: '1px solid #d1d5db', paddingBottom: 6 }}>
              {scopeLabel} · {yearLabel} · {rows.length} payments · twylightcrow.com
            </p>
            <p style={{ fontSize: 14, margin: '0 0 10px', color: '#0f172a' }}>
              {year === 'all' ? 'Lifetime dividends collected' : `Dividends collected in ${year}`}: <b style={{ color: '#059669', fontFamily: 'Menlo, Consolas, monospace' }}>{fmt(shownTotal)}</b>
            </p>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {['Name', 'Paid', 'Amount'].map((h) => (
                    <th key={h} style={{ textAlign: h === 'Amount' ? 'right' : 'left', fontSize: 10, textTransform: 'uppercase', color: '#555', borderBottom: '1px solid #999', padding: '5px 8px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.tx_id || i} className="print-clean" data-pdf-keep style={{ background: '#ffffff' }}>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #e5e5e5', fontSize: 12, color: '#0f172a' }}>
                      <b>{r.ticker || '—'}</b>{r.kind === 'drip' ? ' (DRIP)' : ''}{r.is_roc ? ' (ROC)' : ''}
                      {r.name ? <div style={{ color: '#64748b', fontSize: 11 }}>{r.name}</div> : null}
                    </td>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #e5e5e5', fontSize: 12, whiteSpace: 'nowrap', fontFamily: 'Menlo, Consolas, monospace', color: '#0f172a' }}>{r.date || '—'}</td>
                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #e5e5e5', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 700, fontFamily: 'Menlo, Consolas, monospace', color: '#059669' }}>{fmt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>,
        document.body,
      )}
      {pdfModalOpen && createPortal(
        <div className="fixed inset-0 z-[240] pointer-events-auto" data-testid="dividends-pdf-mode-wrap">
          <PdfModeModal open={pdfModalOpen} onClose={() => setPdfModalOpen(false)} onApply={(mode) => handlePdf(mode)} title="Save / View Dividends Collected PDF" />
        </div>,
        document.body,
      )}
    </Dialog>
  );
}
